import { computePercentiles, findConnectionSetup } from '@/app/pages/Diagnostics/diagnosticsUtils'
import type { ApiMetric } from '@/stores/diagnosticsStore'

export type NetworkQuality = 'unknown' | 'good' | 'fair' | 'poor' | 'offline'

/**
 * The phases a browser's own network panel reports, taken over recent requests instead of one.
 * `dns` and `connect` are pure network: the request has not reached the server yet. `waiting` is
 * network plus server processing and cannot be split further without the server saying how long
 * it took. `download` is network again. The reader compares them, the same inference they would
 * make from a waterfall.
 */
export interface NetworkVerdict {
	quality: NetworkQuality
	dnsMs?: number
	connectMs?: number
	/** How long ago the connection was opened; it is measured once and then reused. */
	connectAgeMs?: number
	waitingMs?: number
	waitingP95Ms?: number
	downloadMs?: number
	requestCount?: number
}

const WINDOW_MS = 5 * 60_000
const MIN_P50_SAMPLES = 3
const MIN_P95_SAMPLES = 20

const GOOD_MAX_MS = 100
const FAIR_MAX_MS = 300

const median = (values: number[]): number | undefined => {
	if (values.length === 0) {
		return undefined
	}
	const sorted = [...values].sort((a, b) => a - b)
	const mid = Math.floor(sorted.length / 2)
	return sorted.length % 2 === 1 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2
}

const qualityFor = (connectMs: number): NetworkQuality => {
	if (connectMs < GOOD_MAX_MS) {
		return 'good'
	}
	return connectMs <= FAIR_MAX_MS ? 'fair' : 'poor'
}

export const networkVerdict = (metrics: ApiMetric[], online: boolean, now: number): NetworkVerdict => {
	if (!online) {
		return { quality: 'offline' }
	}

	// The connection is opened during page load, so requests made afterwards reuse it and report
	// no setup phase. The navigation entry is where that handshake is recorded.
	const setup = findConnectionSetup(now)

	const waiting: number[] = []
	const downloads: number[] = []
	const connects: number[] = []
	const lookups: number[] = []

	for (const m of metrics) {
		if (now - m.timestamp > WINDOW_MS || !m.success) {
			continue
		}
		if (m.ttfb !== undefined) {
			waiting.push(m.ttfb)
		}
		if (m.download !== undefined) {
			downloads.push(m.download)
		}
		if (m.connect !== undefined) {
			connects.push(m.connect)
		}
		if (m.dns !== undefined) {
			lookups.push(m.dns)
		}
	}

	const connectMs = median(connects) ?? setup?.connect
	const percentiles = waiting.length >= MIN_P50_SAMPLES ? computePercentiles(waiting) : undefined

	return {
		quality: connectMs === undefined ? 'unknown' : qualityFor(connectMs),
		dnsMs: median(lookups) ?? setup?.dns,
		connectMs,
		connectAgeMs: connects.length === 0 ? setup?.ageMs : undefined,
		waitingMs: percentiles?.p50,
		waitingP95Ms: waiting.length >= MIN_P95_SAMPLES ? percentiles?.p95 : undefined,
		downloadMs: downloads.length >= MIN_P50_SAMPLES ? median(downloads) : undefined,
		requestCount: waiting.length || undefined,
	}
}
